import { useState, type FC } from "react";

import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { FieldError } from "@/shared/components/ui/FieldError";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { parseAddress, buildAddress, EMPTY_ADDRESS } from "@/shared/utils/addressUtils";
import { isValidEmail } from "@/shared/utils/validators";

import type { PersonContact } from "@/shared/types/estateContact";
import { EditSectionShell } from "./EditSectionShell";

interface ContactInfoEditProps {
  contact: PersonContact;
  onCancel: () => void;
  onSave: (updates: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
}

export const ContactInfoEdit: FC<ContactInfoEditProps> = ({
  contact,
  onCancel,
  onSave,
  isSaving,
}) => {
  const [email, setEmail] = useState(contact.email || "");
  const [phone, setPhone] = useState(contact.phone || "");
  const [address, setAddress] = useState(
    contact.address ? parseAddress(contact.address) : { ...EMPTY_ADDRESS },
  );
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const handleSave = () => {
    let hasError = false;

    if (email && !isValidEmail(email)) {
      setEmailError("Enter a valid email address");
      hasError = true;
    } else {
      setEmailError("");
    }

    if (phone) {
      const digits = phone.replace(/\D/g, "");
      if (digits.length > 0 && digits.length < 10) {
        setPhoneError("Enter a valid 10-digit phone number");
        hasError = true;
      } else {
        setPhoneError("");
      }
    } else {
      setPhoneError("");
    }

    if (hasError) return;

    onSave({
      email: email.trim() || null,
      phone: phone || null,
      address: buildAddress(address) || null,
    });
  };

  return (
    <EditSectionShell
      title="Contact information"
      onCancel={onCancel}
      onSave={handleSave}
      isSaving={isSaving}
    >
      <div className="flex flex-col gap-1.5">
        <Label>Email</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError("");
          }}
          placeholder="Email address"
          className={emailError ? "border-destructive" : ""}
        />
        <FieldError message={emailError} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Cell phone</Label>
        <PhoneNumberInput
          value={phone}
          onChange={(value) => {
            setPhone(value);
            if (phoneError) setPhoneError("");
          }}
          className={phoneError ? "border-destructive" : ""}
        />
        <FieldError message={phoneError} />
      </div>
      <AddressPicker
        value={address}
        onChange={setAddress}
        labels={{ streetAddress: "Mailing address", apartment: "Apt, suite, etc." }}
        stateFormat="abbreviation"
      />
    </EditSectionShell>
  );
};
